/**
 * plan_append_log — Append a timestamped entry to the activity log in a Plan entity.
 *
 * Finds the "Activity Log" section and appends a bullet entry with a timestamp.
 * Creates the section if it doesn't exist.
 */

import { SimpleDocCommand } from '../DocsApp/commands/v5/types';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import { CommandExecutionContext } from '../../types/commands';
import { getApplicationForEntity } from '../../services/applicationRegistry';

export const plan_append_log: SimpleDocCommand = {
  id: 'plan_append_log',
  logicalName: 'plan_append_log',
  version: 'v1',

  description: `Append a timestamped entry to the activity log in a Plan entity.

Use this after every meaningful action to record what happened.
The log is append-only — never overwrite existing entries.

**EXAMPLE:**
{
  "entityId": "plan_cos_setup",
  "entry": "Gmail connected and verified by River."
}`,

  schema: {
    parameters: [
      {
        name: 'entityId',
        type: 'string',
        description: 'Plan entity ID (plan_*)',
        required: true,
      },
      {
        name: 'entry',
        type: 'string',
        description: 'Log entry text (timestamp is added automatically)',
        required: true,
      },
    ],
  },

  examples: {
    good: [{
      input: { entityId: 'plan_cos_setup', entry: 'Bootstrap analysis completed.' },
      expectedOutput: createSuccessResult('Log entry appended', {}),
      description: 'Record bootstrap completion',
    }],
    bad: [],
  },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { entityId, entry } = params;
    if (!entityId) return createErrorResult('Missing entityId', 'entityId is required', 'validation');
    if (!entry) return createErrorResult('Missing entry', 'entry text is required', 'validation');

    try {
      const app = await getApplicationForEntity(entityId);
      if (!app) {
        return createErrorResult('Plan not found', `No application for ${entityId}`, 'validation');
      }

      const timestamp = new Date().toISOString().slice(0, 19).replace('T', ' ');
      const logLine = `${timestamp} — ${entry}`;

      // Find the Activity Log heading to insert after it
      const readResult = await app.handleEntityRead({ all: true }, {});
      const nodes: any[] = readResult.nodes ?? [];

      const activityLogHeadingIdx = nodes.findIndex(
        (n: any) =>
          (n.className === 'page-heading-2' || n.className === 'page-heading-1') &&
          (n.attrs?.text || n.text || '').toLowerCase().includes('activity log')
      );

      if (activityLogHeadingIdx !== -1) {
        // Insert after the last bullet in the activity log section, or right after the heading
        const headingNode = nodes[activityLogHeadingIdx];
        // Find the last bullet after the heading (before next heading)
        let insertAfterNodeId = headingNode.id;
        for (let i = activityLogHeadingIdx + 1; i < nodes.length; i++) {
          const n = nodes[i];
          if (n.className === 'page-heading-1' || n.className === 'page-heading-2') break;
          if (n.className === 'page-bullet' || n.className === 'page-paragraph') {
            insertAfterNodeId = n.id;
          }
        }

        await app.handleEntityWrite({
          operation: 'insert',
          target: { after: insertAfterNodeId },
          content: { className: 'page-bullet', attrs: { text: logLine } },
        });
      } else {
        // No Activity Log section — append at end
        await app.handleEntityWrite({
          operation: 'insert',
          target: { at: 'end' },
          content: { className: 'page-heading-2', attrs: { text: 'Activity Log' } },
        });
        await app.handleEntityWrite({
          operation: 'insert',
          target: { at: 'end' },
          content: { className: 'page-bullet', attrs: { text: logLine } },
        });
      }

      return createSuccessResult('Log entry appended', { entityId, entry: logLine });
    } catch (error: any) {
      return createErrorResult('Failed to append log', error.message || 'Unknown error', 'execution');
    }
  },
};
